import React from 'react';
import { useForm } from 'react-hook-form';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormField,
} from '@/components/ui/form';
import FieldRenderer from './FieldRenderer';

const JsonForm = ({ schema, defaultValues = {}, onSubmit }) => {
  // Setup Ajv validator
  const ajv = new Ajv({ allErrors: true, strict: false });
  addFormats(ajv);
  const validate = ajv.compile(schema);

  // Get default values if not provided
  const inferredDefaults = React.useMemo(() => {
    const defaults = {};
    if (schema.properties) {
      Object.entries(schema.properties).forEach(([key, prop]) => {
        switch (prop.type) {
          case 'string': defaults[key] = ''; break;
          case 'boolean': defaults[key] = false; break;
          case 'number':
          case 'integer': defaults[key] = null; break;
          default: defaults[key] = null;
        }
      });
    }
    return { ...defaults, ...defaultValues };
  }, [schema, defaultValues]);

  // Setup form
  const form = useForm({
    defaultValues: inferredDefaults,
    mode: 'onBlur',
  });

  // Form submission handler
  const handleSubmit = (data) => {
    const valid = validate(data);
    if (valid) {
      onSubmit(data);
    } else {
      // Map Ajv errors to form errors
      (validate.errors || []).forEach(error => {
        const path = error.instancePath.substring(1); // Remove leading slash
        if (path) {
          form.setError(path, {
            type: 'custom',
            message: error.message
          });
        }
      });
    }
  };

  // Render form fields based on schema
  const renderFormFields = () => {
    if (!schema.properties) return null;

    return Object.entries(schema.properties).map(([name, property]) => {
      const isRequired = schema.required?.includes(name);

      return (
        <FormField
          key={name}
          control={form.control}
          name={name}
          rules={{
            required: isRequired ? `${name} is required` : false,
            validate: value => {
              // Field-level validation
              const testObj = { [name]: value };
              const testSchema = {
                type: 'object',
                properties: { [name]: property },
                required: isRequired ? [name] : []
              };
              const validator = ajv.compile(testSchema);
              return validator(testObj) || validator.errors?.[0]?.message;
            }
          }}
          render={({ field }) => (
            <FieldRenderer
              name={name}
              property={property}
              field={field}
              isRequired={isRequired}
            />
          )}
        />
      );
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {renderFormFields()}
        <Button type="submit" className="w-full">Submit</Button>
      </form>
    </Form>
  );
};

export { JsonForm };